import { Component, HostListener, inject, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Subject } from 'rxjs';
import { LocalStorageService } from './connectors/localstorage';
import { BehaviorSubject } from 'rxjs';
import { UserBehaviorService } from './services/user-events/user-behavior.service';
import { LoginService } from './services/login.service';
import { UsersService } from './services/users.service';
import { CryptService } from './services/crypt.service';
import { distinctUntilChanged, take, takeUntil, filter, map } from 'rxjs/operators';
import { ActivityService } from './services/auth/activityService.service';
import { AuthService } from './services/auth/authService.service';
import { Router, NavigationEnd, RouterOutlet, ActivatedRoute } from '@angular/router';
import { HeaderComponent } from './home/shared/components/header/header.component';
import { SideMenuComponent } from './home/shared/components/side-menu/side-menu.component';
import { MenuStateService } from './home/core/services/menu-state.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { NotificationsService } from '@core/notifications/services/notifications.service';
import { PageTitleService } from '@core/services/page-title.service';
import { BroadcastChannelService } from './services/broadcast-channel.service';
import { OverlayContainer } from '@angular/cdk/overlay';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  standalone: true,
  imports: [SideMenuComponent, RouterOutlet, HeaderComponent]
})
export class AppComponent implements OnInit, OnDestroy {
  readonly menuStateService = inject(MenuStateService);
  readonly notificationService = inject(NotificationsService);
  isMenuOpen = toSignal(this.menuStateService.isMenuOpen$, { initialValue: false });
  isLegacyRoute = true;

  globalListenFunc!: Function;

  url: string = this.document.location.href;
  loginPath: boolean = false;

  base_url = window.location.origin;
  pathName = window.location.pathname.split('/');

  loaderSpin: boolean = false;

  timeLeft: number = 20000;
  interval: any;

  user: any;
  email: any;

  innerWidth: any;
  websocketUrl: any;

  downloadable: boolean = false;

  previousUrl: string = '';

  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private localstorage: LocalStorageService,
    private renderer: Renderer2,
    private userBehavior: UserBehaviorService,
    private loginService: LoginService,
    private userService: UsersService,
    private crypt: CryptService,
    private activityService: ActivityService,
    private tokenLogin: AuthService,
    private pageTitleService: PageTitleService,
    private broadcastChannelService: BroadcastChannelService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private overlayContainer: OverlayContainer
  ) {}

  ngOnInit(): void {
    // Sprint 3: Scope CDK overlay portals under .ntt-basecoat-legacy so modals and
    // dropdowns that render outside the page-wrapper DOM tree still receive basecoat styles.
    this.overlayContainer.getContainerElement().classList.add('ntt-basecoat-legacy');

    // Validate access when user logs in
    this.userBehavior.selectedUser.pipe(takeUntil(this.destroy$), distinctUntilChanged()).subscribe((user) => {
      if (user && user?.email) {
        this.email = user?.email;

        this.validateAccess();
      }

      this.chartUrl(window.location.pathname);
    });

    this.userBehavior.localStorageEvents$
      .pipe(
        filter((event) => event.userId),
        take(1)
      )
      .subscribe(() => {
        const userId = Number(this.localstorage.decryptAndGet('userId'));
        this.notificationService.initialize(userId);
      });

    this.globalListenFunc = this.renderer.listen('document', 'mousemove', (e) => {
      this.expiring();
    });

    this.globalListenFunc = this.renderer.listen('document', 'touchstart', (e) => {
      this.expiring();
    });

    this.globalListenFunc = this.renderer.listen('document', 'touchmove', (e) => {
      this.expiring();
    });

    this.globalListenFunc = this.renderer.listen('document', 'touchend', (e) => {
      this.expiring();
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: any) => {
        this.chartUrl(event.urlAfterRedirects);
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) route = route.firstChild;
          return route.snapshot.data;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((data) => {
        this.isLegacyRoute = !data['newUI'];
      });

    // Inicialização baseada no path atual
    this.chartUrl(window.location.pathname);

    /** Listen for successful login messages from other tabs and reload if on login route */
    this.broadcastChannelService.loginSuccess$
      .pipe(
        filter((message) => message !== null && Date.now() - message.timestamp < 5000), // only react to recent login messages to avoid unnecessary reloads
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const pathName = window.location.pathname.split('/');
        if (pathName[1] === 'login') {
          window.location.reload();
        }
      });

    /** Listen for logout initiated messages from other tabs and trigger local logout */
    this.broadcastChannelService.logoutInitiated$
      .pipe(
        filter((message) => message !== null && Date.now() - message.timestamp < 5000),
        takeUntil(this.destroy$)
      )
      .subscribe((message) => {
        const pathName = window.location.pathname.split('/');
        if (pathName[1] !== 'login' && pathName[1] !== '') {
          this.tokenLogin.logout(message!.reason, true); // true = skip broadcast to avoid loops
        }
      });
  }

  validateAccess() {
    this.validateTeams();
    this.validateRoles();
    this.profileCheck();
    this.getUsersByEmail();
  }

  validateTeams(): any {
    this.userService.getUserAccess(this.email).subscribe((resp) => {
      const teams = [
        ...new Set(resp.map((item: any) => (item.statusFlag === '1' ? this.crypt.encryptBy(item.teamName) : null)))
      ];
      if (teams) {
        this.localstorage.set('teams-access', teams);

        this.userBehavior.emitTeamsEvent(true);
      }
    });
  }

  validateRoles(): any {
    this.userService.getUserRoleAccess(this.email).subscribe((resp) => {
      const roles = [
        ...new Set(resp.map((item: any) => (item.statusFlag === '1' ? this.crypt.encryptBy(item.roleName) : null)))
      ];
      if (roles) {
        this.localstorage.set('roles-access', roles);

        this.userBehavior.emitRolesEvent(true);
      }
    });
  }

  profileCheck() {
    this.userService.getProfile(this.email).subscribe((resp) => {
      let cryptedUser = '';
      if (resp.profile.length > 0) {
        cryptedUser = this.crypt.encryptArray(resp.profile);
      } else {
        cryptedUser = this.crypt.encryptBy(resp.profile);
      }

      if (cryptedUser) this.localstorage.set('profile', [cryptedUser]);
    });
  }

  chartUrl(url: string) {
    if (!url) return;

    let path = '';
    try {
      // Se for uma URL completa, extrai o pathname
      if (url.startsWith('http')) {
        path = new URL(url).pathname;
      } else {
        // Se for apenas um path, remove query params e fragmentos
        path = url.split(/[?#]/)[0];
      }
    } catch (e) {
      path = url;
    }

    path = path.toLowerCase();
    
    // Define se estamos em uma página de login ou na raiz (sem sistema logado)
    // Usamos regex ou verificações exatas para evitar falsos positivos
    if (path.includes('/login') || path === '/' || path === '') {
      this.loginPath = true;
    } else {
      this.loginPath = false;
    }
    
  }

  setLoader(url: string) {
    if (
      url.includes('/projects/details') ||
      url.includes('/teams') ||
      url.includes('/roles') ||
      url.includes('/users')
    ) {
      this.loaderSpin = !this.loaderSpin;

      setTimeout(() => {
        this.loaderSpin = false;
      }, 8000);
    }
  }

  expiring(expire?: boolean) {
    const subject = new BehaviorSubject(this.timeLeft);

    if (!expire) {
      this.timeLeft = 20000;
      this.startTimer();
    }

    if (subject.value == 0 || expire) {
      const local = this.localstorage.get('NTTUSLOGIN');
      if (local) {
        this.localstorage.remove('NTTUSLOGIN');
        this.loginService.expire(this.email, 'Y').subscribe();
      }
    }
  }

  startTimer() {
    this.timeLeft = 20000;
    this.interval = setInterval(() => {
      if (this.timeLeft > 0) {
        this.timeLeft--;
        if (this.timeLeft == 0) {
          this.expiring(true);
        }
      } else {
        this.timeLeft = 20000;
      }
    }, 1000);
  }

  async getUsersByEmail() {
    const user = await this.userBehavior.fromBehavior();
    if (!user || !user.email) {
      return;
    }

    this.userService.getUsersByEmail(user.email).subscribe({
      next: (resp: any) => {
        if (!resp || !Array.isArray(resp) || resp.length === 0 || !resp[0].userId) {
          // Ainda assim, tentar continuar fluxo de login sem userId
          this.activityService.setupActivityListeners();
          this.tokenLogin.login(user.email);
          return;
        }

        const cryptedUser = this.crypt.encryptBy(resp[0].userId.toString());

        if (cryptedUser) {
          this.localstorage.set('userId', [cryptedUser]);

          this.userBehavior.emitUserIdEvent(true);
        }

        this.activityService.setupActivityListeners();
        this.tokenLogin.login(user.email);
      },
      error: () => {}
    });
  }

  @HostListener('window:resize', ['$event'])
  listenerWidth(event: any) {
    this.innerWidth = window.innerWidth;
    if (this.innerWidth > 1920) {
      location.reload();
    }
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();

    this.globalListenFunc();
  }
}
